import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { downloadBlob, fetchAttachmentBlob } from '@/lib/downloadFile';
import { formatDate } from '@/lib/format';
import { logger } from '@/lib/logger';
import type { DocumentViewModel } from '@/features/patient-record/types';

import {
  AttachmentPreviewBody,
  type PreviewState,
} from './AttachmentPreviewBody';
import { renderableMimeType } from './previewable';

interface AttachmentPreviewProps {
  readonly document: DocumentViewModel;
  readonly onClose: () => void;
  /** URL of the BE attachment blob — built by `buildAttachmentDownloadUrl`. */
  readonly downloadUrl: string;
}

const INITIAL_STATE: PreviewState = {
  status: 'loading',
  objectUrl: null,
  contentType: null,
};

export function AttachmentPreview({
  document,
  onClose,
  downloadUrl,
}: AttachmentPreviewProps): ReactElement {
  const { t } = useTranslation();
  const [state, setState] = useState<PreviewState>(INITIAL_STATE);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    let createdUrl: string | null = null;

    setState(INITIAL_STATE);

    fetchAttachmentBlob(downloadUrl, controller.signal)
      .then(({ blob, contentType }) => {
        if (cancelled) return;
        // Re-stamp a renderable MIME type when the BE served a generic one
        // (e.g. application/octet-stream); otherwise the iframe/img would
        // download the blob under its UUID instead of rendering it inline.
        const mime = renderableMimeType(contentType, document.fileName);
        const renderable =
          mime !== null && mime !== blob.type ? blob.slice(0, blob.size, mime) : blob;
        createdUrl = URL.createObjectURL(renderable);
        setState({ status: 'ready', objectUrl: createdUrl, contentType });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // AbortError on tab switch / dialog close is expected — keep it out
        // of the error UI. Anything else flips into the user-visible error
        // state.
        if (error instanceof DOMException && error.name === 'AbortError') return;
        logger.warn('attachment.preview.failed', {
          documentId: document.id,
          message: error instanceof Error ? error.message : String(error),
        });
        setState({ status: 'error', objectUrl: null, contentType: null });
      });

    return (): void => {
      cancelled = true;
      controller.abort();
      if (createdUrl !== null) URL.revokeObjectURL(createdUrl);
    };
  }, [downloadUrl, document.id, document.fileName]);

  const onDownload = (): void => {
    if (state.objectUrl !== null) {
      const anchor = window.document.createElement('a');
      anchor.href = state.objectUrl;
      anchor.download = document.fileName ?? document.title;
      window.document.body.appendChild(anchor);
      anchor.click();
      window.document.body.removeChild(anchor);
      return;
    }
    // Preview blob isn't ready (or failed). Hit the URL again — the fetch
    // wrapper inside `downloadBlob` re-runs with credentials.
    downloadBlob(downloadUrl, document.fileName ?? document.title).catch((error: unknown) => {
      logger.warn('attachment.download.failed', {
        documentId: document.id,
        message: error instanceof Error ? error.message : String(error),
      });
    });
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{document.title}</DialogTitle>
          <DialogDescription>
            {t(`patient.documents.type.${document.documentType}`)}
            {' · '}
            {formatDate(document.createdAt)}
            {document.createdBy !== null && (
              <>
                {' · '}
                {t('patient.documents.uploadedBy', { value: document.createdBy })}
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <AttachmentPreviewBody
          state={state}
          fileName={document.fileName}
          title={document.title}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('ui.dialog.close')}
          </Button>
          <Button onClick={onDownload}>
            <Download className="h-4 w-4 mr-1.5" aria-hidden="true" />
            {t('patient.documents.download')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
